import { useState } from "react";
import { Building2, ChevronsUpDown, User, Search } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Input } from "@/components/ui/input";

import { useGetRouterSubAccountsQuery } from "@/api/institutional/router_api";
import { useDispatch, useSelector } from "react-redux";
import {
  GlobalState,
  resetSubaccount,
  setSubaccount,
} from "@/app/slices/globalSlice";
import { useGetUserQuery } from "@/api/institutional/user_api";
import { useTranslation } from "react-i18next";
import { useKeyPress } from "ahooks";
import { ClientStatuses } from "@/interfaces/enums";

export function ClientSwitcher() {
  const { data: user } = useGetUserQuery({});

  const { data: clients } = useGetRouterSubAccountsQuery(undefined, {
    skip: !user?.isRouterBroker,
  });

  const { isMobile } = useSidebar();

  const { subAccount } = useSelector<any, GlobalState>((state) => state.global);

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState("");

  const filteredClients = clients?.filter((client) =>
    client.client_name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const activeClients = filteredClients?.filter(
    (client) => client.status === ClientStatuses.ACTIVE,
  );

  useKeyPress(
    [
      "meta.shift.0",
      "meta.shift.1",
      "meta.shift.2",
      "meta.shift.3",
      "meta.shift.4",
      "meta.shift.5",
      "meta.shift.6",
      "meta.shift.7",
      "meta.shift.8",
      "meta.shift.9",
      "ctrl.shift.0",
      "ctrl.shift.1",
      "ctrl.shift.2",
      "ctrl.shift.3",
      "ctrl.shift.4",
      "ctrl.shift.5",
      "ctrl.shift.6",
      "ctrl.shift.7",
      "ctrl.shift.8",
      "ctrl.shift.9",
    ],
    (event) => {
      const index = Number(event.code.replace("Digit", "")) - 1;
      const client = activeClients?.at(index);
      if (client) {
        dispatch(setSubaccount(client));
        setSearchQuery("");
      }
      if (event.code === "Digit0") {
        dispatch(resetSubaccount());
        setSearchQuery("");
      }
    },
  );

  if (!user?.isRouterBroker && !user?.isSuperBroker) {
    return null;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-sidebar-accent/30 border border-sidebar-border hover:shadow-md transition-shadow data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="text-sidebar-foreground flex aspect-square size-8 items-center justify-center rounded-lg group-data-[collapsible=icon]:aspect-auto">
                {subAccount ? (
                  <Building2 className="size-4" />
                ) : (
                  <User className="size-4" />
                )}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold capitalize">
                  {subAccount?.client_name ||
                    `${user?.name_first || ""} ${user?.name_last || ""}`}
                </span>
                <span className="truncate text-xs text-sidebar-foreground">
                  {subAccount?.sub_account_id}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-56 rounded-lg max-h-[40vh]"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              {t("Clients")}
            </DropdownMenuLabel>
            <div className="p-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("Search clients...")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9"
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            {activeClients?.map((client, index) => (
              <DropdownMenuItem
                key={client.client_name}
                onClick={() => {
                  dispatch(setSubaccount(client));
                  setSearchQuery("");
                }}
                className="gap-2 p-2"
              >
                <div className="flex size-6 items-center justify-center rounded-md border">
                  <Building2 className="size-3.5 shrink-0" />
                </div>
                <span
                  className={
                    client.sub_account_id === subAccount?.sub_account_id
                      ? "font-semibold"
                      : ""
                  }
                >
                  {client.client_name}
                </span>
                {index < 9 && (
                  <DropdownMenuShortcut>⌘+⬆️+{index + 1}</DropdownMenuShortcut>
                )}
              </DropdownMenuItem>
            ))}

            {subAccount?.sub_account_id && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="gap-2 p-2"
                  onClick={() => {
                    dispatch(resetSubaccount());
                    setSearchQuery("");
                  }}
                >
                  <User className="size-4" />
                  <div className="text-muted-foreground ">
                    {t("Clear Client")}
                  </div>
                  <DropdownMenuShortcut>⌘+⬆️+0</DropdownMenuShortcut>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
